import isNil from 'lodash/isNil';
import omit from 'lodash/omit';

import {
  formatDate,
} from 'utils/date';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  isEmptyString,
  notEmptyString,
} from 'utils/misc';
import {
  PatientDTO,
  AddressDTO,
  PayerEntryDTO,
  PagePropertiesRequestDTO,
  MultiMediaResponse,
} from '@/dtos';
import {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import {
  MultimediaType,
  MediaOriginatorType,
} from '@/constants';
import {
  postApiMultimediaRequestMultiMedia,
} from '@/endpoints';

export type Payer = Pick<PayerEntryDTO, 'payerEntryId' | 'payerName' | 'address' | 'insuranceType' | 'isFavorited' | 'isPaper' | 'payerId' | 'searchTerms'>;

export function getPatientAddress(address: Partial<AddressDTO>) {
  return omit(address, ['nickName', 'serviceFacilityName', 'serviceFacilityNpi']);
}

const EMAIL_REGEX = /^(?:[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?)*|"[^"]+")@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
export const PATIENT_NAME_REGEX = /^[^,]*$/;
export function normalizePhoneNumber(phone: string) {
  const phoneNumber = phone?.replace(/\D/g, '');
  return notEmptyString(phoneNumber) && phoneNumber?.length === 11
    ? `+${phoneNumber}` : undefined;
}
export function normalizeNumber(number: string) {
  return number?.replace(/\D/g, '');
}

export const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);
export const validatePhone = (phone?: string | null): boolean => {
  const phoneNumber = normalizePhoneNumber(phone ?? '');
  return /^\+\d{11}$/i.test(phoneNumber ?? '');
};

export const validateSSN = (
  ssn?: string | null,
): boolean => isEmptyString(ssn) || ssn?.length === 9;

export const validateEmailIfSet = (email: string = '') => isEmptyString(email) || validateEmail(email);
export const validatePhoneIfSet = (phone: string | null = '') => {
  const phoneNumber = normalizeNumber(phone ?? '') ?? '';
  return phoneNumber?.substring(1).length === 0 || validatePhone(phoneNumber);
};
export const validateDateRange = (dateRange: Dayjs[]) => (
  dateRange[0]?.isBefore(dateRange[1]) ?? false
);

export function validatePatient(patient: PatientDTO) {
  return isNil(patient?.patientId) ? 'Select patient' : true;
}

export const validateDateIfFuture = (date: Dayjs) => !date.isAfter(formatDate(dayjs()), 'date') && date.isValid();
export const validateDate = (date: Dayjs) => !isNil(date) && dayjs(date)?.isValid();

export function filterAutocompleteOptions<T>(
  options: T[],
  state: FilterOptionsState<T>,
  getSearchableTexts: (option: T) => string[],
) {
  const { inputValue } = state;
  const searchRegex = new RegExp(inputValue.replaceAll('\\', '\\\\'), 'i');
  return isEmptyString(inputValue)
    ? options
    : options.filter((option: T) => {
      const texts = getSearchableTexts(option);
      return texts.some((text) => searchRegex.test(text));
    });
}

export function filterInsurances(options: Payer[], state: FilterOptionsState<Payer>) {
  return filterAutocompleteOptions<Payer>(
    options,
    state,
    (option) => ([option?.payerName ?? '', ...(option?.searchTerms?.split(' ').filter(notEmptyString) ?? [])]),
  );
}

export const getPatientProfileImages = (
  ids: (number | undefined)[] = [],
  pageProperties: PagePropertiesRequestDTO = {},
  generateUrls = true,
): Promise<MultiMediaResponse[]> => {
  const compacted = compact(ids);

  return compacted.length === 0
    ? Promise.resolve([])
    : postApiMultimediaRequestMultiMedia({
      generateUrls,
      ...(isNil(pageProperties) || isEmpty(pageProperties)
        ? {}
        : { pageProperties }
      ),
      list: compacted
        .map((id) => ({
          originatorId: id,
          originatorType: MediaOriginatorType.Patient,
          multimediaType: MultimediaType.ProfilePicture,
        })),
    }).then((res) => res?.list ?? []);
};